import axios from "axios";
import config from "../config";
import { clearToken } from "./utility";

const SESSION_HEADER = "X-Parse-Session-Token";

const storedSessionToken = () => {
    try {
        const user = JSON.parse(localStorage.getItem("currentUser") || "null");
        return user && user.sessionToken;
    } catch (error) {
        return null;
    }
};

const withoutSessionHeader = (headers = {}) => Object.keys(headers).reduce((result, key) => {
    if (key.toLowerCase() !== SESSION_HEADER.toLowerCase()) {
        const value = headers[key];
        result[key] = value && typeof value === "object" ? withoutSessionHeader(value) : value;
    }
    return result;
}, {});

const returnToLogin = () => {
    clearToken();
    const fromPath = window.location.pathname === "/login"
        ? new URLSearchParams(window.location.search).get("fromPath")
        : window.location.pathname.replace(/^\/+/, "");
    window.location.replace(`/login${fromPath ? `?fromPath=${encodeURIComponent(fromPath)}` : ""}`);
};

export const createApiClient = ({ apiUrls, readSessionToken = storedSessionToken, onSessionRequired = returnToLogin }) => {
    const allowedApis = apiUrls.map((url) => new URL(url));
    const client = axios.create();
    let rejectedSessionToken;
    const isAllowedRequest = (request) => {
        try {
            const url = new URL(request.url, request.baseURL || window.location.href);
            return !url.username && !url.password && allowedApis.some((api) => {
                const prefix = api.pathname.replace(/\/$/, "");
                return url.origin === api.origin && (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`));
            });
        } catch (error) {
            return false;
        }
    };
    client.interceptors.request.use((request) => {
        const headers = withoutSessionHeader(request.headers);
        if (isAllowedRequest(request)) {
            const token = readSessionToken();
            if (typeof token === "string" && token.length > 0 && !/[\r\n]/.test(token)) {
                headers[SESSION_HEADER] = token;
            }
        }
        return { ...request, headers };
    });
    client.interceptors.response.use(response => response, error => {
        const response = error && error.response;
        const request = error && error.config;
        if (response && response.status === 401 && response.data && response.data.code === "SESSION_REQUIRED" && isAllowedRequest(request)) {
            const headers = request.headers || {};
            const key = Object.keys(headers).find(name => name.toLowerCase() === SESSION_HEADER.toLowerCase());
            const token = key && headers[key];
            // A late response from a previous login must not clear the new session.
            if (typeof token === "string" && token && token === readSessionToken() && token !== rejectedSessionToken) {
                rejectedSessionToken = token;
                onSessionRequired();
            }
        }
        return Promise.reject(error);
    });
    return client;
};

export default createApiClient({ apiUrls: [config.apiUrl, config.loadBalance] });
